import { ReactNode } from 'react';
import { array } from 'fp-ts';
import { flow } from 'fp-ts/function';
import { TextKey } from '@mews-commander/enums';
import { Card, Divider, Stack } from '@optimus-web/core';
import { PaymentMethod, PaymentMethodDetailResult } from '@mews-commander/data-access';
import { LocalizedLabel, LocalizedSmallLabel } from '@mews-commander/ui';
import { intersperse } from '@mews/fp';
import { WithAccountId } from '@mews-commander/types';
import { hasPaymentMethodDetailStatusHistory } from './utils';
import { PaymentMethodDetailStatusHistory } from './PaymentMethodDetailStatusHistory/PaymentMethodDetailStatusHistory';
import { useGetPaymentMethodEntries } from './hooks';

interface Props extends WithAccountId {
    paymentMethod: PaymentMethodDetailResult;
    paymentMethodType: PaymentMethod;
    countryName?: string;
}

interface ListEntryProps {
    textKey: Mews.TextKey;
    value: ReactNode;
}

const ListEntry = ({ textKey, value }: ListEntryProps) => (
    <Stack spacing="500" distribution="space-between">
        <LocalizedLabel textKey={textKey} variant="secondary" as="div" />
        {value}
    </Stack>
);

const renderEntries = flow(
    array.map((entryProps: ListEntryProps) => <ListEntry key={entryProps.textKey} {...entryProps} />),
    intersperse((index: number) => <Divider key={index} />)
);

export const PaymentMethodDetailsSidebarContent = ({ paymentMethod, paymentMethodType, accountId }: Props) => {
    const entries = useGetPaymentMethodEntries({ paymentMethod, accountId });

    if (!entries) {
        return null;
    }

    return (
        <Stack vertical spacing="200">
            <Card padding="200">
                <Stack vertical spacing="200">
                    <LocalizedSmallLabel textKey={TextKey.DetailPlural} as="div" />
                    <Stack vertical spacing="100">
                        {renderEntries(entries)}
                    </Stack>
                </Stack>
            </Card>
            {hasPaymentMethodDetailStatusHistory(paymentMethod) && (
                <Card padding="200">
                    <Stack vertical spacing="200">
                        <LocalizedSmallLabel textKey={TextKey.StatusHistory} as="div" />
                        <PaymentMethodDetailStatusHistory
                            paymentMethod={paymentMethod}
                            paymentMethodType={paymentMethodType}
                        />
                    </Stack>
                </Card>
            )}
        </Stack>
    );
};
